import { SupportedTokenAllowance, SupportedTokenName } from "@mezo/sdk"
import { createSlice } from "@reduxjs/toolkit"

import { WalletBalances } from "#/shared/types"

export type WalletState = {
  isConnected: boolean
  isProcessingTransaction: boolean
  chainId: number | null
  address: string | null
  evmAddress: string | null
  btcAddress: string | null
  name: string | null
  balances: WalletBalances
  tokenAllowance: SupportedTokenAllowance
  depositFromAnotherWallet: boolean
  hasLoadedWalletBalances: boolean
}

const initialWalletBalances: WalletBalances = {
  BTC: { balance: 0n, decimals: 8n },
  tBTC: { balance: 0n, decimals: 18n },
  WBTC: { balance: 0n, decimals: 8n },
  fBTC: { balance: 0n, decimals: 8n },
  SolvBTC: { balance: 0n, decimals: 18n },
  xSolvBTC: { balance: 0n, decimals: 18n },
  thUSD: { balance: 0n, decimals: 18n },
  USDe: { balance: 0n, decimals: 18n },
  USDC: { balance: 0n, decimals: 6n },
  USDT: { balance: 0n, decimals: 6n },
  crvUSD: { balance: 0n, decimals: 18n },
  DAI: { balance: 0n, decimals: 18n },
  stBTC: { balance: 0n, decimals: 18n },
  "crv-stBTC": { balance: 0n, decimals: 18n },
  swBTC: { balance: 0n, decimals: 8n },
  cbBTC: { balance: 0n, decimals: 8n },
  LBTC: { balance: 0n, decimals: 8n },
  aBTC: { balance: 0n, decimals: 18n },
  intBTC: { balance: 0n, decimals: 18n },
  wM: { balance: 0n, decimals: 6n },
  T: { balance: 0n, decimals: 18n },
  mBTC: { balance: 0n, decimals: 18n },
  MUSD: { balance: 0n, decimals: 18n },
}

const initialTokenAllowance: SupportedTokenAllowance = {
  BTC: 0n,
  tBTC: 0n,
  WBTC: 0n,
  fBTC: 0n,
  SolvBTC: 0n,
  xSolvBTC: 0n,
  thUSD: 0n,
  USDe: 0n,
  USDC: 0n,
  USDT: 0n,
  crvUSD: 0n,
  DAI: 0n,
  stBTC: 0n,
  "crv-stBTC": 0n,
  swBTC: 0n,
  cbBTC: 0n,
  LBTC: 0n,
  aBTC: 0n,
  intBTC: 0n,
  wM: 0n,
  T: 0n,
  mBTC: 0n,
  MUSD: 0n,
}

const initialState: WalletState = {
  isConnected: false,
  isProcessingTransaction: false,
  chainId: null,
  address: null,
  evmAddress: null,
  btcAddress: null,
  name: null,
  balances: initialWalletBalances,
  tokenAllowance: initialTokenAllowance,
  depositFromAnotherWallet: false,
  hasLoadedWalletBalances: false,
}

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    setConnectedWallet: (
      immerState,
      {
        payload,
      }: {
        payload: {
          evmAddress: string
          btcAddress: string | null
          name: string | null | undefined
        }
      },
    ) => {
      immerState.isConnected = true
      immerState.address = payload.btcAddress ?? payload.evmAddress
      immerState.evmAddress = payload.evmAddress
      immerState.btcAddress = payload.btcAddress ?? null
      immerState.name = payload.name ?? null
    },
    setConnectedChainId: (
      immerState,
      { payload }: { payload: number | null },
    ) => {
      immerState.chainId = payload
    },
    setWalletBalances: (
      immerState,
      { payload }: { payload: Partial<WalletBalances> },
    ) => {
      immerState.balances = { ...immerState.balances, ...payload }
    },
    setTokenAllowance: (
      immerState,
      {
        payload,
      }: { payload: Partial<{ [key in SupportedTokenName]: bigint }> },
    ) => {
      immerState.tokenAllowance = { ...immerState.tokenAllowance, ...payload }
    },
    setIsProcessingTransaction: (
      immerState,
      { payload }: { payload: boolean },
    ) => {
      immerState.isProcessingTransaction = payload
    },
    setDepositFromAnotherWallet: (
      immerState,
      { payload }: { payload: boolean },
    ) => {
      immerState.depositFromAnotherWallet = payload
    },
    setHasLoadedWalletBalances: (
      immerState,
      { payload }: { payload: boolean },
    ) => {
      immerState.hasLoadedWalletBalances = payload
    },
    resetWallet: () => initialState,
  },
})

export const {
  setConnectedWallet,
  setConnectedChainId,
  setWalletBalances,
  setTokenAllowance,
  setIsProcessingTransaction,
  setDepositFromAnotherWallet,
  setHasLoadedWalletBalances,
  resetWallet,
} = walletSlice.actions

export default walletSlice.reducer
